import React from "react";

import { Button, Input, Row, Col, Form } from "antd";
import { template, Validators, PASSWORD_REGEXP } from "@reco-m/core";
import { EditComponent, Countdown } from "@reco-w/core-ui";

import { Namespaces, passwordModel } from "@reco-w/auth-models";

export namespace PasswordCommon {
    export interface IProps<S extends IState = IState> extends EditComponent.IProps<S> {}

    export interface IState extends EditComponent.IState, passwordModel.StateType {}

    export class Component<P extends IProps = IProps, S extends IState = IState> extends EditComponent.Base<P, S> {
        namespace = Namespaces.password;
        showloading = false;
        /**
         * 判断是否是第一次校验手机号
         */
        mobileFirstCheck = false;

        componentMount() {
            window.scroll(0, 0);
        }

        componentDidMount(): void {
            this.dispatch({ type: "initPage" });
        }

        componentWillUnmount() {
            this.dispatch({ type: "initState" });
        }

        /**
         * 校验手机号
         */
        phoneValidator() {
            const { cellphone, required, composeControl } = Validators,
                { state } = this.props;

            return composeControl([required, cellphone], { value: state!.phone, name: "手机号码" });
        }

        /**
         * 校验手机
         * @returns
         */
        validatePhone(): boolean {
            const msg = this.phoneValidator()!();

            if (msg) {
                return true;
            }

            return false;
        }

        /**
         * 验证码发送前校验 及 发送
         * @param delay 延迟时间方法
         */
        sendVerifyCode(delay: Function) {
            const msg = this.phoneValidator()!();

            if (msg) {
                this.message.error(msg.msg.join());
                return;
            }

            const { state } = this.props;

            this.dispatch({
                type: "sendVerifyCode",
                data: { mobile: state!.phone },
                delay,
            });
        }

        /**
         * 获取手机号表单状态及提示语
         */
        getValidateStatusAndHelp() {
            const { state } = this.props,
                phone = state!.phone;
            if (!phone && this.mobileFirstCheck) {
                return { status: "error", help: "手机号不能为空!" };
            } else {
                return { status: "validating", help: "" };
            }
        }

        /**
         * 渲染手机号
         */
        renderMobile(): React.ReactNode {
            const { state } = this.props;
            const validate = this.getValidateStatusAndHelp();
            return (
                <Form.Item label="手机号" required={true} validateStatus={validate?.status as any} help={validate?.help || null}>
                    <Input
                        size="large"
                        placeholder="请输入手机号"
                        value={state!.phone}
                        disabled={!!state!.currentUser}
                        onChange={(e) => {
                            this.mobileFirstCheck = true;
                            this.dispatch({ type: "input", data: { phone: e.target.value } });
                        }}
                    />
                </Form.Item>
            );
        }

        /**
         * 渲染验证码
         */
        renderVerifyCode(): React.ReactNode {
            return (
                <Form.Item label="验证码" name="code" rules={[{ required: true, message: "请输入获取的验证码!" }]}>
                    <Row gutter={8}>
                        <Col span={17}>
                            <Input
                                size="large"
                                placeholder="请输入验证码"
                                onChange={(e) => {
                                    this.dispatch({ type: "input", data: { code: e.target.value } });
                                }}
                            />
                        </Col>
                        <Countdown.Component
                            className="size-14 grayColor3"
                            start={this.sendVerifyCode.bind(this)}
                            content="发送验证码"
                            isButton={true}
                            buttonSize="large"
                            buttonType="primary"
                            disabled={this.validatePhone()}
                            isUseImageVertify={true}
                        />
                    </Row>
                </Form.Item>
            );
        }

        /**
         * 渲染新密码
         */
        renderPassword(): React.ReactNode {
            return (
                <Form.Item
                    label="新密码"
                    name="password"
                    rules={[
                        { required: true, message: "新密码不能为空!" },
                        ({}) => ({
                            validator(_rule, value) {
                                if (value && !PASSWORD_REGEXP.exec(value)) {
                                    return Promise.reject("请输入密码（8-32位字母数字和特殊字符组合）");
                                } else {
                                    return Promise.resolve();
                                }
                            },
                        }),
                    ]}
                >
                    <Input
                        size="large"
                        placeholder="请输入密码（8-32位字母数字和特殊字符组合）"
                        autoComplete="new-password" // 阻止浏览器在password类型的input中自动赋值
                        name="password"
                        type="password"
                        onChange={(e) => {
                            this.dispatch({ type: "input", data: { password: e.target.value } });
                        }}
                    />
                </Form.Item>
            );
        }

        /**
         * 渲染确认密码
         */
        renderConfirmPassword(): React.ReactNode {
            return (
                <Form.Item
                    label="确认密码"
                    name="confirm"
                    rules={[
                        { required: true, message: "确认密码不能为空!" },
                        ({ getFieldValue }) => ({
                            validator(_rule, value) {
                                if (value && value !== getFieldValue("password")) {
                                    return Promise.reject("两次输入的密码不一致！");
                                } else {
                                    return Promise.resolve();
                                }
                            },
                        }),
                    ]}
                >
                    <Input
                        size="large"
                        name="confirm-password"
                        autoComplete="new-password" // 阻止浏览器在password类型的input中自动赋值
                        type="password"
                        placeholder="请再次输入新密码确认"
                        onChange={(e) => {
                            this.dispatch({ type: "input", data: { confirm: e.target.value } });
                        }}
                    />
                </Form.Item>
            );
        }

        /**
         * 渲染填写表单
         */
        renderForm() {
            const formItemLayout = {
                labelCol: { span: 6 },
                wrapperCol: { span: 18 },
            };
            return (
                <Row justify="space-around" align="middle">
                    <Col span={15}>
                        <Form {...formItemLayout}>
                            {this.renderMobile()}
                            {this.renderVerifyCode()}
                            {this.renderPassword()}
                            {this.renderConfirmPassword()}
                        </Form>
                    </Col>
                </Row>
            );
        }

        /**
         * 数据校验
         */
        validator() {
            const { required, cellphone, composeControl, ValidatorControl } = Validators,
                { state } = this.props;

            return ValidatorControl(
                composeControl([required, cellphone], { value: state!.phone, name: "手机号码" }),
                composeControl([required], { value: state!.code, name: "验证码" }),
                composeControl([required], { value: state!.password, name: "新密码" }),
                composeControl([required], { value: state!.confirm, name: "确认密码" })
            );
        }

        /**
         * 提交
         */
        submit() {
            const msg = this.validator()!();
            if (msg) {
                this.message.error(msg.join());
                return;
            }

            const { state } = this.props;
            if (state!.password !== state!.confirm) {
                this.message.error("两次密码输入不一致");
                return;
            }

            if (!PASSWORD_REGEXP.exec(state!.password)) {
                this.message.error("密码修改失败：密码必须输入8~32个字母数字和特殊字符");
                return;
            }

            this.dispatch({
                type: "submit",
                callback: () => this.submitSuccessCallback(),
            });
        }

        /**
         * 提交成功回调
         */
        submitSuccessCallback() {}

        /**
         * 渲染提交按钮
         */
        renderButton() {
            const { state } = this.props,
                isSubmit = state!.isSubmit;
            const tailLayout = {
                wrapperCol: {
                    offset: 6,
                    span: 18,
                },
            };
            return (
                <Row justify="space-around" align="middle">
                    <Col span={15}>
                        <Form.Item {...tailLayout}>
                            <Button loading={isSubmit} type="primary" onClick={this.submit.bind(this)}>
                                提交
                            </Button>
                        </Form.Item>
                    </Col>
                </Row>
            );
        }
    }

    export const Page = template(Component, (state) => state[Namespaces.password]);
}
